import { z } from 'zod';

const errorDataSchema = z.object({
  statusCode: z.number().optional(),
  code: z.unknown().optional(),
  description: z.string().nullable().optional(),
});

/** 서버 HttpExceptionFilter 응답 — { ok: false, data: { statusCode, code, description } } */
const errorBodySchema = z.union([z.object({ data: errorDataSchema }), errorDataSchema]);

export const NETWORK_ERROR = 'NETWORK_ERROR';
export const TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS';
export const UNKNOWN_ERROR = 'UNKNOWN_ERROR';

/** API 오류 — 화면은 code 로 문구·다음 행동을 고른다 (shared/api/error-messages.ts) */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly description: string | null;

  constructor(params: { status: number; code: string; description?: string | null }) {
    super(params.description ?? params.code);
    this.name = 'ApiError';
    this.status = params.status;
    this.code = params.code;
    this.description = params.description ?? null;
  }

  /** 서버가 준 오류 본문을 해석한다 — 모양이 달라도 status 로 최소한의 오류를 만든다 */
  static fromResponse(response: Response, body: unknown): ApiError {
    if (response.status === 429) {
      return new ApiError({ status: 429, code: TOO_MANY_REQUESTS });
    }
    const parsed = errorBodySchema.safeParse(body);
    const data = parsed.success ? ('data' in parsed.data ? parsed.data.data : parsed.data) : null;
    return new ApiError({
      status: response.status,
      code: typeof data?.code === 'string' ? data.code : UNKNOWN_ERROR,
      description: data?.description ?? null,
    });
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** 특정 코드의 API 오류인지 */
export function hasErrorCode(error: unknown, ...codes: string[]): error is ApiError {
  return isApiError(error) && codes.includes(error.code);
}
